import React, { useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Cell,
  Tooltip
} from 'recharts'
import { Box, Stack } from '@mui/material'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import EmptyScreen from 'packages/ui/EmptyScreen'
import ChartSkeleton from 'components/global/CUK/Skeletons/ChartSkeleton'

import { formatBytesWithUnit } from '../../../helpers'
import EvolutionTooltip from './EvolutionTooltip'
import useGraph from './use-graph'

const EvolutionConsumption = () => {
  const { t, i18n } = useTranslation()
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const {loading, data} = useGraph()

  const evolutionData = useMemo(() => {
    return data.map(item => ({
      date: item.date,
      monthName: moment(item.date).format('MMM'),
      consumption_past_year: item.consumption_past_year || null,
      consumption_current_year: item.consumption_current_year || null,
      percentage: item.percentage_consumption.toFixed(0),
      hasCurrentData: item.consumption_current_year > 0,
      hasPastData: item.consumption_past_year > 0
    }))
  }, [data])

  if (loading) {
    return <ChartSkeleton />
  }

  if (!data.length) {
    return (
      <Box sx={{ 
        width: '100%',
        height: '100%',
        border: '1px dashed #D7DAEA',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px'
      }}>
        <EmptyScreen 
          icon="square-poll-vertical" 
          variant="compact"
          iconSize="32px" 
          fontSize="14px"
          spacing={1.5}
        >
          {t('stats.evolution.no_data')}
        </EmptyScreen>
      </Box>
    )
  }

  return (
    <Stack height={'100%'}>
    <ResponsiveContainer>
      <BarChart
        width={500}
        height={300}
        data={evolutionData}
        margin={{
          top: 20,
          right: 5,
          left: 20,
          bottom: 5,
        }}
        barCategoryGap={20}
        onMouseMove={(data) => {
          if (data && data.activeTooltipIndex !== undefined) {
            setHoveredIndex(data.activeTooltipIndex)
          }
        }}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <XAxis 
          dataKey="monthName" 
          style={{ fontSize: '0.75rem', fontWeight: 400 }}
        />
        <YAxis 
          style={{ fontSize: '0.75rem', fontWeight: 400 }}
          tickFormatter={(value) => {
            const { value: formattedValue, unit } = formatBytesWithUnit(value, i18n.language)
            return `${formattedValue}${unit}`
          }}
        />
        <Tooltip
          cursor={false}
          wrapperStyle={{ outline: 'none', zIndex: 9999 }}
          content={<EvolutionTooltip isActiveUsers={false} />}
          position={{ y: -140 }}
        />
        <Bar dataKey="consumption_past_year" fill="#A5D9CB" radius={[4, 4, 4, 4]} maxBarSize={16} barSize={16}>
          {evolutionData.map((entry, index) => {
            const isHovered = hoveredIndex === index
            return (
              <Cell 
                key={`cell-past-${index}`} 
                fillOpacity={!entry.hasPastData ? 0.3 : (hoveredIndex === null ? 1 : (isHovered ? 1 : 0.3))}
              />
            )
          })}
        </Bar>
        <Bar dataKey="consumption_current_year" fill="#4F8AA4" radius={[4, 4, 4, 4]} maxBarSize={16} barSize={16}>
          {evolutionData.map((entry, index) => {
            const isHovered = hoveredIndex === index
            return (
              <Cell 
                key={`cell-current-${index}`} 
                fillOpacity={!entry.hasCurrentData ? 0.3 : (hoveredIndex === null ? 1 : (isHovered ? 1 : 0.3))}
              />
            )
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
    </Stack>
  )
}

export default EvolutionConsumption